import {AccessType} from '@/features/access-types/types'
import {REPORT_TEXTAREA_LIMIT} from '@/features/medical-reports/formSchemas'
import {AccessFrequency} from '@/features/planned-accesses/types'
import i18n from '@/translations/i18n'
import {z} from 'zod'
import {NotificationUsersConfirmationValidationSchema} from '../users/types'

const BaseAccessSchema = z.object({
    accessType: AccessType.optional(),
    numberOfAccesses: z
        .number()
        .int()
        .positive({message: i18n.t('errors:positive_number')})
        .or(z.literal(''))
        .optional()
})

const InPersonAccessesSchema = BaseAccessSchema.extend({
    frequency: AccessFrequency,
    sector: z.enum(['outpatient', 'domiciliary']).optional()
})

export const PlannedAccessesFieldsSchema = z
    .object({
        id: z.string().min(1),
        projectId: z.string().min(1),
        isMixedProject: z.boolean(),
        inPersonAccesses: z.array(
            InPersonAccessesSchema.refine(values => !!values.accessType, {
                path: ['accessType'],
                message: i18n.t('errors:required')
            })
        ),
        singleAccesses: z.array(
            BaseAccessSchema.refine(values => !!values.accessType, {
                path: ['accessType'],
                message: i18n.t('errors:required')
            }).refine(values => !!values.numberOfAccesses, {
                path: ['numberOfAccesses'],
                message: i18n.t('errors:required')
            })
        ),
        absenceAccesses: z.array(
            BaseAccessSchema.refine(values => !!values.accessType, {
                path: ['accessType'],
                message: i18n.t('errors:required')
            }).refine(values => !!values.numberOfAccesses, {
                path: ['numberOfAccesses'],
                message: i18n.t('errors:required')
            })
        ),
        treatmentsPlan: z.string().max(REPORT_TEXTAREA_LIMIT).optional(),
        objectives: z.string().max(REPORT_TEXTAREA_LIMIT).optional(),
        additionalNotes: z.string().max(REPORT_TEXTAREA_LIMIT).optional()
    })
    .merge(NotificationUsersConfirmationValidationSchema)
export type PlannedAccessesFieldsSchema = z.infer<typeof PlannedAccessesFieldsSchema>

export const PlannedAccessesFieldsSchemaMixedProject = z.discriminatedUnion('isMixedProject', [
    z
        .object({
            isMixedProject: z.literal(true)
        })
        .merge(
            z.object({
                inPersonAccesses: z.array(
                    InPersonAccessesSchema.refine(values => !!values.accessType, {
                        path: ['accessType'],
                        message: i18n.t('errors:required')
                    })
                        .refine(values => !!values.numberOfAccesses, {
                            path: ['numberOfAccesses'],
                            message: i18n.t('errors:required')
                        })
                        .refine(values => !!values.sector, {
                            path: ['sector'],
                            message: i18n.t('errors:required')
                        })
                )
            })
        )
])

export const PlannedAccessesFieldsSchemaNoMixedProject = z.discriminatedUnion('isMixedProject', [
    z
        .object({
            isMixedProject: z.literal(false)
        })
        .merge(
            z.object({
                inPersonAccesses: z.array(
                    InPersonAccessesSchema.refine(values => !!values.accessType, {
                        path: ['accessType'],
                        message: i18n.t('errors:required')
                    }).refine(values => !!values.numberOfAccesses, {
                        path: ['numberOfAccesses'],
                        message: i18n.t('errors:required')
                    })
                )
            })
        )
])

export const CreatePlannedAccessFormValidationSchema = PlannedAccessesFieldsSchema.pick({
    projectId: true,
    singleAccesses: true,
    absenceAccesses: true,
    treatmentsPlan: true,
    objectives: true,
    isMixedProject: true,
    additionalNotes: true,
    inPersonAccesses: true
}).and(
    z.discriminatedUnion('isMixedProject', [
        ...PlannedAccessesFieldsSchemaMixedProject.options,
        ...PlannedAccessesFieldsSchemaNoMixedProject.options
    ])
)
export type CreatePlannedAccessFormValidationSchema = z.infer<typeof CreatePlannedAccessFormValidationSchema>

export const CreatePlannedAccessConfirmationSchema = CreatePlannedAccessFormValidationSchema.and(
    PlannedAccessesFieldsSchema.pick({
        notificationsRecipients: true,
        notificationDetails: true
    })
)
export type CreatePlannedAccessConfirmationSchema = z.infer<typeof CreatePlannedAccessConfirmationSchema>

export const EditPlannedAccessFormValidationSchema = PlannedAccessesFieldsSchema.pick({
    id: true,
    projectId: true,
    isMixedProject: true,
    singleAccesses: true,
    absenceAccesses: true,
    treatmentsPlan: true,
    objectives: true,
    additionalNotes: true
}).and(
    z.discriminatedUnion('isMixedProject', [
        ...PlannedAccessesFieldsSchemaMixedProject.options,
        ...PlannedAccessesFieldsSchemaNoMixedProject.options
    ])
)
export type EditPlannedAccessFormValidationSchema = z.infer<typeof EditPlannedAccessFormValidationSchema>

export const EditPlannedAccessConfirmationSchema = EditPlannedAccessFormValidationSchema.and(
    PlannedAccessesFieldsSchema.pick({
        notificationsRecipients: true,
        notificationDetails: true
    })
)
export type EditPlannedAccessConfirmationSchema = z.infer<typeof EditPlannedAccessConfirmationSchema>
